import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowRight, RotateCcw } from "lucide-react";

const AREAS = [
  ["Cellular Repair", "Recovery"], ["Cognition", "Cognition"], ["Metabolism", "Metabolic"],
  ["Aging Biology", "Longevity"], ["Inflammatory Pathways", "Inflammation"],
  ["Performance Biology", "Performance"], ["Skin Biology", "Skin"],
];
const EVIDENCE = ["Preclinical", "Clinical", "Mechanistic", "Comparative"];

export default function Match() {
  const [peptides, setPeptides] = useState([]);
  const [area, setArea] = useState(null);
  const [evidence, setEvidence] = useState(null);

  useEffect(() => { api.get("/peptides").then(({ data }) => setPeptides(data)).catch(() => {}); }, []);

  const matches = area
    ? peptides.filter((p) => (p.categories || []).some((c) => c.toLowerCase() === area[1].toLowerCase()))
    : [];

  return (
    <div className="px-4 sm:px-8 lg:px-12 pt-28 pb-20 max-w-5xl mx-auto">
      <div className="mono-label mb-3">Research discovery</div>
      <h1 className="font-serif text-5xl sm:text-6xl text-white mb-3">Research Match</h1>
      <p className="text-slate-400 text-sm max-w-xl mb-10">Explore research subjects aligned with your interests. This tool does not diagnose, prescribe or recommend dosages.</p>

      <div className="glass rounded-3xl p-8 mb-6">
        <div className="mono-label mb-4">01 · What biological area are you researching?</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {AREAS.map(([label, key]) => (
            <button key={key} onClick={() => setArea([label, key])} data-testid={`match-area-${key.toLowerCase()}`}
              className={`p-4 rounded-2xl border text-left transition-all ${area?.[1] === key ? "border-cyan-400/60 bg-cyan-400/10" : "border-white/10 bg-white/5 hover:border-cyan-400/30"}`}>
              <div className="text-sm font-medium text-white">{label}</div>
            </button>
          ))}
        </div>
      </div>

      {area && (
        <div className="glass rounded-3xl p-8 mb-6">
          <div className="mono-label mb-4">02 · What evidence type interests you?</div>
          <div className="flex flex-wrap gap-3">
            {EVIDENCE.map((e) => (
              <button key={e} onClick={() => setEvidence(e)} data-testid={`match-evidence-${e.toLowerCase()}`}
                className={`px-5 py-2.5 rounded-full border text-sm font-mono transition-all ${evidence === e ? "border-violet-400/60 bg-violet-400/10 text-violet-300" : "border-white/10 bg-white/5 text-slate-300 hover:border-violet-400/30"}`}>
                {e}
              </button>
            ))}
          </div>
        </div>
      )}

      {area && (
        <div className="glass rounded-3xl p-8">
          <div className="flex items-center justify-between mb-4">
            <div className="mono-label">Results · {area[0]}</div>
            <button onClick={() => { setArea(null); setEvidence(null); }} className="text-xs font-mono text-slate-500 flex items-center gap-1"><RotateCcw size={11} /> Reset</button>
          </div>
          {matches.length === 0 && <p className="text-slate-500 text-sm">No research subjects matched this area yet.</p>}
          <div className="space-y-3">
            {matches.map((p) => (
              <div key={p.slug} className="border border-white/10 rounded-2xl p-4 flex items-center justify-between" data-testid={`match-result-${p.slug}`}>
                <div>
                  <div className="font-serif text-xl text-white">{p.name}</div>
                  <div className="text-xs text-slate-500 mt-0.5">Matched because it is classified under <span className="text-cyan-300 font-mono">{area[0]}</span> in the CG Labz taxonomy.</div>
                </div>
                <Link to={`/peptides/${p.slug}`} className="text-xs font-mono text-cyan-300 flex items-center gap-1 shrink-0 ml-4">Explore <ArrowRight size={11} /></Link>
              </div>
            ))}
          </div>
          <p className="mt-5 text-[11px] font-mono text-slate-500">For research reference only. Not medical advice.</p>
        </div>
      )}
    </div>
  );
}
